/**
 * @fileoverview Control Panel — the governance and administration surface, at
 * `/workspace/control-panel/` and its enrollment-scoped `/home` route.
 *
 * BUILT AGAINST THE LIVE TENANT, not against a class-name outline. I rebuilt
 * this twice from `foundry-ui`'s static capture by reading module names and
 * inferring a layout, and was wrong both times — first the shell nesting, then
 * the whole page. `fidelity/capture-control-panel.mjs` opens the real thing at
 * `/workspace/control-panel`; what it shows is a HOME DASHBOARD, and what I had
 * built was a Multipass settings list with a roles table on top.
 *
 * The real landing page, from that capture:
 *
 *   rail (230px, light)   Home · All Settings · Approvals · Your Plan · Search
 *                         "FAVORITE SETTINGS" → View all settings
 *                         footer: AIP Assist · Help · Open Foundry · the user
 *   content               hero title + one-line subtitle, illustration right
 *                         a full-width "What setting are you looking for?" field
 *                         ENROLLMENT DETAILS card
 *                         "Getting started" — a two-column grid of task cards
 *                         "Favorites" — with a See-all-settings link
 *                         "Resources"
 *
 * EVERY COMPONENT THIS USES ALREADY EXISTED. `control-panel-home-hero`,
 * `getting-started-card`, `enrollment-details-card`, `favorites-empty-state`,
 * `resource-card`, `card-section-header`, the `settings-sidebar*` family — 25
 * files under components/control-panel, built for this exact surface, and
 * nothing rendered any of them. The route mounted the Multipass settings
 * components instead. That is why the page looked invented: it was.
 *
 * WHAT IS OURS RATHER THAN FOUNDRY'S. The getting-started tasks point at pages
 * this product actually has (roles, users, markings, organizations) instead of
 * Foundry's enrollment and network-ingress ones, and "Your Plan" is absent —
 * there is no billing surface behind it, and a rail entry that leads nowhere is
 * worse than one that is missing. Enrollment is the agency: this app's tenant
 * boundary is the agency id on the session, which is the same idea under a
 * different name.
 */

import { useEffect } from "react";
import { AnchorButton, Button, Divider, H2, Icon, KeyComboTag } from "@blueprintjs/core";
import { Link, useLocation, useNavigate } from "react-router";

import { Module } from "../components/module";
import { useAuth } from "../contexts/auth-context";
import {
  CardSectionHeader,
  EnrollmentDetailsCard,
  FavoritesEmptyState,
  GettingStartedCard,
  ResourceCard,
  SettingsSidebar,
  SettingsSidebarFooter,
  SettingsSidebarLink,
  SettingsSidebarFavorites,
  SettingsSidebarSection,
} from "../components/control-panel";
import {
  controlPanelApp as cpa,
  controlPanelShell as fdy,
} from "../components/control-panel/foundry-parts.generated";
import { LoadingBar } from "../packages/control-panel-shell/src/components/LoadingBar";
import { useOrganizations } from "../lib/hooks";
import { SearchBar } from "../packages/control-panel-shell/src/components/SearchBar";
import { ControlPanelHeader } from "../packages/control-panel-shell/src/components/ControlPanelHeader";
import { WorkspaceBanners } from "../packages/control-panel-shell/src/components/WorkspaceBanners";
import { container } from "../hex/container";
import { controlPanelPath } from "../lib/control-panel-routes";

/**
 * The getting-started tasks.
 *
 * Foundry's are enrollment, contact information and network ingress. Ours are
 * the four administrative jobs this product actually supports, each pointing at
 * a page that exists — a task card that opens a placeholder is an instruction to
 * do something the software cannot do.
 */
const TASKS = [
  {
    icon: "people" as const,
    title: "Manage users and roles",
    description: "Add investigators, assign roles, and review who has access.",
    route: "roles",
    scope: "enrollment" as const,
  },
  {
    icon: "shield" as const,
    title: "Assign organization permissions",
    description: "Choose which agencies an administrator can manage.",
    route: "organizations",
    scope: "enrollment" as const,
  },
  {
    icon: "label" as const,
    title: "Review classification markings",
    description: "Manage the markings that gate evidence and case content.",
    route: "markings",
    scope: "organization" as const,
  },
  {
    icon: "key" as const,
    title: "Configure authentication",
    description: "Identity providers, passkeys, and second factors.",
    route: "authentication",
    scope: "enrollment" as const,
  },
  {
    icon: "application" as const,
    title: "Review authorized applications",
    description: "See which applications may act on this agency's behalf.",
    route: "authorized-applications",
    scope: "enrollment" as const,
  },
];

export function ControlPanel() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { session } = useAuth();
  const organizations = useOrganizations();
  const config = container().config;
  const enrollmentPath = (route: string) => controlPanelPath(config, "enrollment", route);
  const organizationPath = (route: string) => controlPanelPath(config, "organization", route);

  // Authenticated source capture (2026-08-01): the application document at
  // /workspace/control-panel/ settles on the enrollment-scoped /home route.
  // Keep the document URL as the entry contract, then perform the same
  // replace-style client navigation once deployment identity is available.
  useEffect(() => {
    if (/^\/workspace\/control-panel\/?$/.test(pathname)) {
      navigate(enrollmentPath("home"), { replace: true });
    }
  }, [pathname, navigate, config.controlPanelEnrollmentRid]);

  // Enrollment is the agency — the same idea under Foundry's name for it.
  const agency = session?.agencyId ?? "No agency";

  return (
    <Module data-testid="control-panel" header={null}>
      <div className={`bp-control-panel ${fdy["settings-wrapper"]}`}>
        {/*
          THE BANNER WRAPPER. `settings-wrapper > banner-wrapper > [banner, top
          bar, settings]` — measured at 900px against a 900px viewport, so it is
          the full-height column, not a strip above the content. We had the
          header as a direct child of settings-wrapper, which put every sibling
          a level too shallow AND truncated the DOM comparison at eighteen
          nodes, so nothing below it was being measured.
        */}
        <WorkspaceBanners text="UNCLASSIFIED // NOTIONAL DATA">
        {/*
          THE CONTROL PANEL DRAWS ITS OWN TOP BAR. The generic ModuleHeader
          carries a centred Search box with no counterpart on the tenant, and
          the picker hung beneath it as a second row — two stacked bars where
          the reference has one. See ControlPanelHeader.
        */}
        {/*
          ENROLLMENT IS THE DEPLOYMENT, ORGANIZATION IS THE AGENCY. I had both
          bound to `agency`, which rendered the breadcrumb as the agency
          containing itself. Foundry's reads enrollment `droneconnection` /
          organization `droneconnection` only because their tenant and their org
          share a name; ours do not.
        */}
        <ControlPanelHeader enrollmentName="IronCase" organizationName={agency} />
        {/*
          NOTE: this frame is a SECOND implementation of the one in
          components/multipass/settings-app-shell.tsx — the Control Panel home
          and the settings module each build `__settings-wrapper__ /
          __settings__ / sidebar / content` themselves. Fixing the sidebar
          nesting meant fixing it twice, which is the tell. They should be one
          component; that is a larger change than this one.
        */}
        <div data-test-id="settings-container" className={fdy["settings"]}>
          <LoadingBar />
            <SettingsSidebar>
              <SettingsSidebarSection>
                <SettingsSidebarLink icon="home" name="Home" active href={enrollmentPath("home")} />
                <SettingsSidebarLink
                  // `layout-grid`, which is what the tenant renders for All
                  // settings; `grid-view` is a different Blueprint glyph.
                  icon="layout-grid"
                  name="All Settings"
                  href={enrollmentPath("all-settings")}
                />
                {/*
                  No "Your Plan". Foundry's rail has one and there is no billing
                  surface behind ours; a rail entry that leads nowhere is worse
                  than one that is absent, because it costs a click to learn.
                */}
                <SettingsSidebarLink
                  icon="search"
                  name="Search"
                  keyCombo="ctrl+j"
                  href={enrollmentPath("all-settings")}
                />
              </SettingsSidebarSection>
              {/*
                A "View all settings" LINK stood in for the whole Favorite
                settings group: the capture has a header with its own "View all"
                action and a bordered empty-state button inside a group, not a
                rail row. See settings-sidebar-favorites.tsx.
              */}
              <Divider />
              <SettingsSidebarFavorites allSettingsHref={enrollmentPath("all-settings")} />
              <SettingsSidebarFooter>
                {/*
                  THE WAY OUT. This surface owns the viewport and the workspace
                  rail is not rendered beside it, exactly as the tenant does it —
                  so leaving has to be part of the app. Upstream the same footer
                  slot carries "Open Foundry"; ours goes home.
                */}
                <SettingsSidebarLink icon="undo" name="Back to IronCase" href="/" />
                <SettingsSidebarLink icon="help" name="Help" href="/help" />
                {/*
                  THE USER ITEM IS NOT A RAIL LINK. The capture ends the footer
                  with a popover target carrying an avatar and the signed-in
                  person's name — `control-panel-app__user-sidebar-item__fay7tg`
                  — not another icon-and-label row.

                  Only that one class is vendored; the `user-account-menu__*`
                  parts belong to a namespace our captures do not cover, and
                  naming them here would emit classes that resolve to nothing.
                */}
                <Link
                  to="/profile"
                  className={`${cpa["user-sidebar-item"]} bp6-popover-target`}
                  data-testid="control-panel-user-item"
                >
                  <Icon icon="user" size={20} />
                  {session?.email ?? "Profile"}
                </Link>
              </SettingsSidebarFooter>
            </SettingsSidebar>

          {/*
            `__container__` wraps `__settings-content__`. Read off a structural
            snapshot of the tenant's own All Settings page, not guessed: the
            content column is a flex-column container holding the scrolling
            content, and we were rendering the inner element directly, so
            everything under it sat one level shallow.
          */}
          <div className={fdy["container@t7drfl"]}>
          <div className={fdy["settings-content"]}>
            <div className="bp-control-panel-home">
              {/*
                PORTED FROM THE RECONSTRUCTION, not inferred from a diff.
                ../palantir/packages/foundry/packages/foundry-apps/control-panel/
                src/control-panel-app/ControlPanelHome.tsx is the recovered
                component, and its top level is a `__header__` block holding the
                title/image row and the search bar, then a `__container__`
                holding the four sections. We had the hero and the search as
                siblings with no wrappers, which is why the diff reported this
                whole region as missing nodes.

                All of these parts are 1wd12z. The SECTION headings inside the
                container are `header@1gh3j8l` — a different module's `header`,
                and the reason the generator refuses the unqualified name.
              */}
              <div className={cpa["header@1wd12z"]}>
                <div className={cpa["title-container"]}>
                  <div className={cpa["title-and-image"]}>
                    <div className={cpa["title-and-description"]}>
                      {/*
                        H2 and subtitle DIRECTLY, as ControlPanelHome.tsx has
                        them. Wrapping them in ControlPanelHomeHero put its own
                        <header> and an inner div between this element and the
                        heading — two levels deeper than Foundry, wearing its own
                        layout rules inside Foundry's, which is what broke the
                        rendered page. The component is still used elsewhere; it
                        just is not what belongs here.
                      */}
                      <H2>Control Panel</H2>
                      <div className={cpa["subtitle"]}>
                        Governance and administration for this agency.
                      </div>
                    </div>
                    {/*
                      No hero image. Upstream renders one here; we have no
                      artwork for it, and an empty <img> to match a node count
                      would be a broken image on the page.
                    */}
                  </div>
                  <div className={cpa["search-bar"]}>
                    <SearchBar onOpen={() => navigate(enrollmentPath("all-settings"))} />
                  </div>
                </div>
              </div>

              <div className={cpa["container@1wd12z"]}>
              {/*
                THE ORGANIZATIONS SECTION IS HALF THIS CARD, and we were passing
                none — the component has supported `organizations` all along, so
                the section simply never rendered. The capture has enrollment
                details, a divider, then Organizations with a row per org and an
                info affordance on each.
              */}
              <EnrollmentDetailsCard
                enrollmentName="IronCase"
                defaultDomain="ironcase.app"
                onShowDetails={() => navigate(enrollmentPath("organizations"))}
                organizations={(organizations.data ?? []).map((o) => ({
                  id: o.id,
                  name: o.name,
                  icon: "office" as const,
                  onInfo: () => navigate(enrollmentPath("organizations")),
                }))}
              />

              {/*
                EACH SECTION IS A DIV WRAPPING ITS HEADER AND BODY. They were
                siblings — header, body, header, body — and the capture nests
                them, so every section was one level shallow and the diff
                reported the whole content column as missing nodes.
              */}
              <div>
              <CardSectionHeader
                title="Getting started"
                action={
                  <Button
                    outlined
                    icon="search"
                    // No class: the reconstruction uses control-panel-app__key-combo__1rfjnmb
                    // and our vendored parts do not carry that module, so naming it
                    // here would be a class that resolves to nothing.
                    rightIcon={<KeyComboTag combo="ctrl + J" minimal />}
                    onClick={() => navigate(enrollmentPath("all-settings"))}
                  >
                    Search
                  </Button>
                }
              />
              {/* An unclassed div sits between every section header and its
                  container in the capture. */}
              <div>
              <div className={`${cpa["links-container"]} bp-control-panel-home-tasks`}>
                {TASKS.map((t) => (
                  <GettingStartedCard
                    key={`${t.scope}:${t.route}`}
                    illustration={<Icon icon={t.icon} size={20} />}
                    title={t.title}
                    description={t.description}
                    href={controlPanelPath(config, t.scope, t.route)}
                  />
                ))}
              </div>
              </div>
              </div>

              <div>
              <CardSectionHeader
                title="Favorites"
                action={
                  <AnchorButton
                    minimal
                    intent="primary"
                    rightIcon="arrow-right"
                    href={enrollmentPath("all-settings")}
                  >
                    See all settings
                  </AnchorButton>
                }
              />
              <div>
              {/*
                The empty state carries a CTA in the capture — a primary button
                into All Settings. An empty state that only describes the
                emptiness leaves the reader with nowhere to go.
              */}
              <FavoritesEmptyState
                description="Favorite your frequently visited settings for easy access"
                icon="star-empty"
                action={
                  <AnchorButton
                    intent="primary"
                    icon="plus"
                    rightIcon="arrow-right"
                    href={enrollmentPath("all-settings")}
                  >
                    View all settings
                  </AnchorButton>
                }
              />
              </div>
              </div>

              <div>
              <CardSectionHeader title="Resources" />
              <div>
              <div className={`${cpa["resources-container"]} bp-control-panel-home-resources`}>
                <ResourceCard
                  icon="manual"
                  tone="violet"
                  title="Administration guide"
                  description="How roles, markings and organizations fit together."
                  actionLabel="Read the guide"
                  href="/help"
                />
                <ResourceCard
                  icon="shield"
                  tone="turquoise"
                  title="Access review"
                  description="Check who can see case and evidence content."
                  actionLabel="Review access"
                  href={enrollmentPath("roles")}
                />
                {/* Three, as the capture has three. */}
                <ResourceCard
                  icon="shield"
                  tone="gray"
                  title="Security fundamentals"
                  description="How markings, roles and organizations combine to control access."
                  actionLabel="Learn more"
                  href={organizationPath("markings")}
                />
              </div>
              </div>
              </div>
              </div>
            </div>
          </div>
          </div>
        </div>
        </WorkspaceBanners>
      </div>
    </Module>
  );
}
